import React, { useState, useCallback, useEffect, createContext, useContext } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'auth_tokens';
const USER_KEY = 'auth_user';

function getStoredTokens() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser);
  const [loading, setLoading] = useState(true);
  const [requires2faSetup, setRequires2faSetup] = useState(false);

  // Set up axios interceptor for auth headers
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use((config) => {
      const tokens = getStoredTokens();
      if (tokens?.access) {
        config.headers.Authorization = `Bearer ${tokens.access}`;
      }
      return config;
    });

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const tokens = getStoredTokens();
          if (tokens?.refresh) {
            try {
              const res = await api.post('/auth/token/refresh/', {
                refresh: tokens.refresh,
              });
              const newTokens = { access: res.data.access, refresh: res.data.refresh || tokens.refresh };
              localStorage.setItem(TOKEN_KEY, JSON.stringify(newTokens));
              originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
              return api(originalRequest);
            } catch {
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(USER_KEY);
              setUser(null);
              setRequires2faSetup(false);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Verify stored tokens on mount
  useEffect(() => {
    const tokens = getStoredTokens();
    if (tokens?.access) {
      api.get('/auth/me/', {
        headers: { Authorization: `Bearer ${tokens.access}` },
        timeout: 10000,
      })
        .then((res) => {
          setUser(res.data);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data));
          if (!res.data.has_2fa) {
            setRequires2faSetup(true);
          }
        })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async ({ username, password, otp_code, temp_token, remember_browser }) => {
    const res = await api.post('/auth/login/', { username, password, otp_code, temp_token, remember_browser });
    const data = res.data;

    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }

    // Got JWT tokens
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ access: data.access, refresh: data.refresh }));
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);

    if (data.requires_2fa_setup) {
      setRequires2faSetup(true);
      return { requires_2fa_setup: true };
    }

    setRequires2faSetup(false);
    return { success: true };
  }, []);

  const complete2faSetup = useCallback(() => {
    setRequires2faSetup(false);
    // Refresh user data
    api.get('/auth/me/').then((res) => {
      setUser(res.data);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data));
    });
  }, []);

  const logout = useCallback(async () => {
    const tokens = getStoredTokens();
    try {
      if (tokens?.refresh) {
        await api.post('/auth/logout/', { refresh: tokens.refresh });
      }
    } catch {
      // ignore
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setRequires2faSetup(false);
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    complete2faSetup,
    isAuthenticated: Boolean(user),
    requires2faSetup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
